import {
  ATTEMPT_DERIVED_STATUS_COLORS_MAP,
  ATTEMPT_DERIVED_STATUS_LABEL,
  ISSUE_STATUSES_KEYED,
} from "@miplatform/constants";

export const CLOSEOUT_TAB = {
  TASKS: "Tasks",
  DOCUMENTS: "Documents",
  DETAILS: "Details",
  HISTORY: "History",
  ACTIVITY: "Activity",
  CONTACTS: "Contacts",
};

export const CLOSEOUT_TABS = [
  { label: CLOSEOUT_TAB.TASKS, value: CLOSEOUT_TAB.TASKS },
  { label: CLOSEOUT_TAB.DOCUMENTS, value: CLOSEOUT_TAB.DOCUMENTS },
  { label: CLOSEOUT_TAB.DETAILS, value: CLOSEOUT_TAB.DETAILS },
  { label: CLOSEOUT_TAB.HISTORY, value: CLOSEOUT_TAB.HISTORY },
  {
    label: CLOSEOUT_TAB.ACTIVITY,
    value: CLOSEOUT_TAB.ACTIVITY,
  },
  {
    label: CLOSEOUT_TAB.CONTACTS,
    value: CLOSEOUT_TAB.CONTACTS,
  },
];

export const CLOSEOUT_SCREENS = {
  MAIN: "MAIN",
  CLOSEOUT: "CLOSEOUT",
  REVIEW: "REVIEW",
  WO_REVIEW: "WO_REVIEW",
  PREBILL: "PREBILL",
};

export const CLOSING_OUT_VIEWS = {
  ISSUE_REVIEW: "issue_review",
  LABOR_INVOICE: "labor_invoice",
  MATERIAL_INVOICE: "material_invoice",
  DISCOUNT_INVOICE: "discount_invoice",
};

export const ASSIGNMENTS_PRINT_HEADER_FIELDS = [
  {
    label: "PO Number",
  },
  {
    label: "Order #",
    field: "attempt.assignment.assignmentNumber",
  },
];

export const ASSIGNMENT_ASSIGNMENT_STATUS_LABEL = "Order Status";

export const ASSIGNMENT_STATUS_LABEL = "Status";

const GREY_FIELD_SET = new Set([
  ASSIGNMENT_ASSIGNMENT_STATUS_LABEL,
  ASSIGNMENT_STATUS_LABEL,
]);

export const ASSIGNMENTS_PRINT_STATUS_FIELD = {
  label: "Order Status:",
  valueGetter: ({ attempt: { derivedStatus } }) => ({
    status: ATTEMPT_DERIVED_STATUS_LABEL[derivedStatus],
    color: ATTEMPT_DERIVED_STATUS_COLORS_MAP[derivedStatus]?.main,
  }),
};

export const ASSIGNMENTS_PRINT_SECTIONS = [
  {
    title: "Bill to",
    width: "49%",
    columns: [
      [{ label: "Customer", field: "account.name" }],
      [
        {
          label: "Bill to Address",
          valueGetter: ({ account: { address } }) => (
            <>
              {address?.addressLine1 || "-"}
              <br />
              {address?.addressLine2 && (
                <>
                  {address?.addressLine2}
                  <br />
                </>
              )}
              {address?.city &&
                `${address?.city}, ${address?.regionCode}, ${address?.postalCode}`}
            </>
          ),
        },
      ],
    ],
  },
  {
    title: "Request Details",
    width: "49%",
    columns: [
      [
        {
          label: "Address",
          valueGetter: ({
            attempt: {
              home: { address },
            },
          }) => (
            <>
              {address?.addressLine1 || "-"}
              <br />
              {address?.addressLine2 && (
                <>
                  {address?.addressLine2}
                  <br />
                </>
              )}
              {address?.city &&
                `${address?.city}, ${address?.regionCode}, ${address?.postalCode}`}
            </>
          ),
        },
        {
          label: "Reported By",
          valueGetter: ({ order: { requester } }) => (
            <>
              {requester?.firstName
                ? `${requester.firstName} ${requester.lastName}`
                : "-"}
              <br />
              {requester?.title && (
                <>
                  <br />
                  {requester?.title}
                </>
              )}
              {requester?.email && (
                <>
                  <br />
                  {requester?.email}
                </>
              )}
              {requester?.phone && (
                <>
                  <br />
                  {requester?.phone}
                </>
              )}
            </>
          ),
        },
      ],
      [
        {
          label: "Community",
          field: "attempt.home.communityName",
        },
        {
          label: "Dispatcher",
          field: "attempt.dispatcherName",
        },
        {
          label: "Service Technician",
          field: "attempt.assignedUserName",
        },
      ],
    ],
  },
  {
    title: "Work Performed",
    getCellClassName: (params) => {
      if (GREY_FIELD_SET.has(params.field)) {
        return "grey bold";
      }

      return null;
    },
    tables: [
      {
        field: "attempt.issues",
        columns: [
          {
            label: "Task",
            field: "title",
            valueGetter: ({ issueResult, title }) =>
              issueResult?.title || title || "-",
            width: 120,
          },
          {
            label: "Area",
            field: "areaName",
            valueGetter: ({ issueResult, areaName }) =>
              issueResult?.areaName || areaName || "-",
            width: 120,
          },
          {
            label: "Description",
            field: "originalDescription",
            valueGetter: ({ issueResult, originalDescription }) =>
              issueResult?.originalDescription || originalDescription || "-",
            width: 410,
          },
          {
            label: ASSIGNMENT_STATUS_LABEL,
            field: "status",
            valueGetter: ({ issueResult, status }) => {
              const issueStatus = issueResult?.status || status;

              return ISSUE_STATUSES_KEYED[issueStatus]?.label || "-";
            },
            width: 120,
          },
        ],
      },
    ],
    rowTables: [
      [
        {
          label: "Type",
          field: "attempt.assignment.assignmentTemplateName",
          width: 145,
        },
        {
          label: "Order Notes",
          field: "order.notes",
          defaultValue: "There is no notes for this order.",
          width: 505,
        },
        {
          label: ASSIGNMENT_ASSIGNMENT_STATUS_LABEL,
          field: "attempt.derivedStatus",
          valueGetter: ({ attempt }) =>
            ATTEMPT_DERIVED_STATUS_LABEL[attempt.derivedStatus],
          width: 120,
        },
      ],
    ],
  },
];

export const INVOICE_ITEM_TYPE = {
  MATERIAL: "Material",
  LABOR: "Labor",
  DISCOUNT: "Discount",
  OTHER: "Other",
};

export const MINIMIZE_CLOSEOUT_DRAWER_CONFIG = {
  [CLOSEOUT_SCREENS.CLOSEOUT]: {
    title: "Minimize Active Closeout",
    description:
      "You are minimizing an active closeout. This will keep the order in progress and show the card as active on the calendar. Continue?",
  },
  [CLOSEOUT_SCREENS.PREBILL]: {
    title: "Minimize Pre-Bill",
    description:
      "You are minimizing pre-billing. This will keep the order in pending pre-bill and show the card as active on the calendar. Continue?",
  },
  default: {
    title: "Minimize Drawer",
    description:
      "You are minimizing the drawer. The card will remain active on the calendar. Continue?",
  },
};
